import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format, differenceInDays, parseISO } from "date-fns";
import { 
  Plus, 
  Calendar as CalendarIcon, 
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  FolderKanban,
  GripVertical,
  Flag,
  Target,
  Send,
  BarChart3,
  List,
  Download,
  Users,
  FileSpreadsheet
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableTaskItem({ task, user, handleStatusChange, openEditDialog, handleDeleteTask }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const assigneeDisplay = (task.assignee_names || []).join(", ") || "Unassigned";
  const canEdit = user?.role === 'admin' || (task.assignee_ids || []).includes(user?.id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors shadow-sm ${
        task.escalated ? 'border-l-4 border-l-red-500' : ''
      } ${task.is_critical ? 'ring-1 ring-amber-400' : ''}`}
      data-testid={`task-${task.id}`}
    >
      <div className="flex items-start gap-3">
        {user?.role === 'admin' && (
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 shrink-0 mt-1">
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {/* Title and badges */}
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-semibold text-sm text-slate-900">{task.title}</h4>
            {task.is_critical && (
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 rounded-md text-xs">
                <Flag className="w-3 h-3 mr-1" />Critical
              </Badge>
            )}
            {task.escalated && (
              <Badge className="bg-red-50 text-red-700 border-red-200 rounded-md text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" />Escalated
              </Badge>
            )}
            {task.depends_on_title && (
              <Badge className="bg-purple-50 text-purple-700 border-purple-200 rounded-md text-xs">
                After: {task.depends_on_title}
              </Badge>
            )}
          </div>

          {/* Assignees and Date */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
            <span className="flex items-center gap-1.5" title={assigneeDisplay}>
              <Users className="w-3.5 h-3.5" />
              <span className="truncate max-w-[200px]">{assigneeDisplay}</span>
            </span>
            <span className="flex items-center gap-1 shrink-0">
              <CalendarIcon className="w-3.5 h-3.5" />
              {task.start_date && `${new Date(task.start_date).toLocaleDateString('en-GB', {day:'2-digit',month:'short'})} - `}
              {new Date(task.due_date).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'})}
            </span>
          </div>

          {/* Actions Row */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <Select value={task.status} onValueChange={(value) => handleStatusChange(task.id, value)} disabled={!canEdit}>
              <SelectTrigger className="w-[130px] h-8 rounded-lg border-slate-200 text-xs" data-testid={`status-${task.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="pending"><span className="flex items-center gap-2 text-xs"><Clock className="w-3 h-3 text-amber-600" />Pending</span></SelectItem>
                <SelectItem value="in_progress"><span className="flex items-center gap-2 text-xs"><Clock className="w-3 h-3 text-blue-600" />In Progress</span></SelectItem>
                <SelectItem value="completed"><span className="flex items-center gap-2 text-xs"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Completed</span></SelectItem>
              </SelectContent>
            </Select>

            {canEdit && (
              <div className="flex items-center gap-1 ml-auto">
                <Button variant="ghost" size="icon" onClick={() => openEditDialog(task)} className="rounded-lg h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid={`edit-${task.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                {user?.role === 'admin' && (
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteTask(task.id)} className="rounded-lg h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-${task.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Multi-select assignee component
function AssigneeMultiSelect({ users, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const selectedNames = users.filter(u => selectedIds.includes(u.id)).map(u => u.name);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start text-left font-normal rounded-lg h-11 border-slate-200" data-testid="task-assignee-multi-select">
          <Users className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">
            {selectedNames.length > 0 ? selectedNames.join(", ") : "Select assignees"}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-2 bg-white border-slate-200 max-h-[250px] overflow-y-auto" align="start">
        {users.map(u => (
          <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox
              checked={selectedIds.includes(u.id)}
              onCheckedChange={(checked) => {
                if (checked) onChange([...selectedIds, u.id]);
                else onChange(selectedIds.filter(id => id !== u.id));
              }}
            />
            <span className="truncate">{u.name}</span>
            <span className="text-xs text-slate-400 ml-auto shrink-0">{u.department}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { user, token, API } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [sortBy, setSortBy] = useState("due_date");
  const [expandedDepts, setExpandedDepts] = useState({});
  const [earliestOpeningDate, setEarliestOpeningDate] = useState(null);
  const [criticalTaskCount, setCriticalTaskCount] = useState(0);
  const [activeView, setActiveView] = useState("tasks");
  const [ganttData, setGanttData] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [showDateChangeReason, setShowDateChangeReason] = useState(false);
  const [originalDueDate, setOriginalDueDate] = useState(null);
  const [showEditProjectDialog, setShowEditProjectDialog] = useState(false);
  const [projectFormData, setProjectFormData] = useState({ product: "", location: "" });

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    department: "",
    assignee_ids: [],
    due_date: "",
    start_date: "",
    depends_on: "",
    date_change_reason: ""
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { fetchData(); fetchAllTasks(); }, [projectId, sortBy]);

  const fetchAllTasks = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/tasks/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) setAllTasks(await response.json());
    } catch (error) { console.error("Failed to fetch tasks list"); }
  };

  const fetchData = async () => {
    try {
      const [projectRes, deptsRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}/tasks?sort_by=${sortBy}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/departments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (projectRes.ok) {
        const data = await projectRes.json();
        setProject(data.project);
        setDepartments(data.departments || []);
        setEarliestOpeningDate(data.earliest_opening_date);
        setCriticalTaskCount(data.critical_task_count || 0);
        const expanded = {};
        data.departments?.forEach(d => { expanded[d.department] = true; });
        setExpandedDepts(expanded);
      }
      if (deptsRes.ok) {
        const data = await deptsRes.json();
        setAllDepartments(data.departments || []);
      }
      if (user?.role === 'admin') {
        const usersRes = await fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
        if (usersRes.ok) setUsers(await usersRes.json());
      } else {
        setUsers([{ id: user.id, name: user.name, department: user.department }]);
      }
    } catch (error) { toast.error("Failed to load project data"); }
    finally { setLoading(false); }
  };

  const fetchGanttData = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/gantt`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setGanttData(await response.json());
    } catch (error) { toast.error("Failed to load Gantt data"); }
  };

  const handlePublish = async () => {
    if (!confirm("This will send email notifications to all team members with their assigned tasks. Continue?")) return;
    setPublishing(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/publish`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to publish");
      toast.success(`Published! ${data.emails_sent} emails sent, ${data.emails_failed} failed`);
      fetchData();
    } catch (error) { toast.error(error.message); }
    finally { setPublishing(false); }
  };

  const handleDownloadReport = async () => {
    try {
      toast.info("Generating report...");
      const response = await fetch(`${API}/projects/${projectId}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate report");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name || 'Project'}_Report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (error) { toast.error(error.message); }
  };

  const handleDownloadExcel = async () => {
    try {
      toast.info("Generating Excel...");
      const response = await fetch(`${API}/projects/${projectId}/export.xlsx`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate Excel");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name || 'Project'}_Tasks.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (error) { toast.error(error.message); }
  };

  const openEditProjectDialog = () => {
    setProjectFormData({ product: project.product || "", location: project.location || "" });
    setShowEditProjectDialog(true);
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    if (!projectFormData.product || !projectFormData.location) { toast.error("Please fill in both fields"); return; }
    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(projectFormData)
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Failed"); }
      toast.success("Project updated");
      setShowEditProjectDialog(false);
      fetchData();
    } catch (error) { toast.error(error.message); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    let activeTask = null, activeDeptIndex = -1, activeTaskIndex = -1;
    departments.forEach((dept, dIndex) => {
      dept.tasks.forEach((task, tIndex) => {
        if (task.id === active.id) { activeTask = task; activeDeptIndex = dIndex; activeTaskIndex = tIndex; }
      });
    });
    if (!activeTask) return;
    let overDeptIndex = -1, overTaskIndex = -1;
    departments.forEach((dept, dIndex) => {
      dept.tasks.forEach((task, tIndex) => {
        if (task.id === over.id) { overDeptIndex = dIndex; overTaskIndex = tIndex; }
      });
    });
    if (overDeptIndex === -1) return;
    const newDepartments = [...departments];
    if (activeDeptIndex === overDeptIndex) {
      newDepartments[activeDeptIndex].tasks = arrayMove(newDepartments[activeDeptIndex].tasks, activeTaskIndex, overTaskIndex);
    } else {
      const [movedTask] = newDepartments[activeDeptIndex].tasks.splice(activeTaskIndex, 1);
      movedTask.department = newDepartments[overDeptIndex].department;
      newDepartments[overDeptIndex].tasks.splice(overTaskIndex, 0, movedTask);
      newDepartments[activeDeptIndex].total--;
      newDepartments[overDeptIndex].total++;
      if (movedTask.status === 'completed') { newDepartments[activeDeptIndex].completed--; newDepartments[overDeptIndex].completed++; }
      try {
        await fetch(`${API}/tasks/${active.id}/reorder`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ task_id: active.id, new_department: newDepartments[overDeptIndex].department, new_order: overTaskIndex })
        });
        toast.success("Task moved to " + newDepartments[overDeptIndex].department);
      } catch (error) { toast.error("Failed to update task"); fetchData(); return; }
    }
    setDepartments(newDepartments);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.department || formData.assignee_ids.length === 0 || !formData.due_date) {
      toast.error("Please fill all required fields (title, department, assignee, due date)");
      return;
    }
    try {
      const url = editingTask ? `${API}/tasks/${editingTask.id}` : `${API}/tasks`;
      const method = editingTask ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          department: formData.department,
          assignee_ids: formData.assignee_ids,
          project_id: projectId,
          due_date: formData.due_date,
          start_date: formData.start_date || null,
          depends_on: formData.depends_on || null,
          date_change_reason: formData.date_change_reason || null
        })
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Failed to save task"); }
      toast.success(editingTask ? "Task updated" : "Task created");
      setShowDialog(false);
      resetForm();
      fetchData();
      fetchAllTasks();
    } catch (error) { toast.error(error.message); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error("Failed to update status");
      toast.success("Status updated");
      fetchData();
    } catch (error) { toast.error(error.message); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm("Delete this task?")) return;
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Failed to delete");
      toast.success("Task deleted");
      fetchData();
    } catch (error) { toast.error(error.message); }
  };

  const resetForm = () => {
    setFormData({
      title: "", description: "",
      department: user?.role !== 'admin' ? user?.department || "" : "",
      assignee_ids: user?.role !== 'admin' ? [user?.id] : [],
      due_date: "", start_date: "", depends_on: "", date_change_reason: ""
    });
    setEditingTask(null);
    setShowDateChangeReason(false);
    setOriginalDueDate(null);
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setOriginalDueDate(task.due_date);
    setFormData({
      title: task.title,
      description: task.description,
      department: task.department,
      assignee_ids: task.assignee_ids || [],
      due_date: task.due_date?.substring(0, 10) || "",
      start_date: task.start_date?.substring(0, 10) || "",
      depends_on: task.depends_on || "",
      date_change_reason: ""
    });
    setShowDateChangeReason(false);
    setShowDialog(true);
  };

  const handleDueDateChange = (val) => {
    setFormData({ ...formData, due_date: val });
    if (editingTask && originalDueDate && val < originalDueDate) setShowDateChangeReason(true);
    else setShowDateChangeReason(false);
  };

  const toggleDept = (dept) => setExpandedDepts(prev => ({ ...prev, [dept]: !prev[dept] }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-500">Loading project...</div></div>;
  if (!project) return (
    <div className="flex flex-col items-center justify-center h-64">
      <p className="text-slate-500 mb-4">Project not found</p>
      <Button onClick={() => navigate('/projects')} variant="outline" className="border-slate-300">Back to Projects</Button>
    </div>
  );

  const totalTasks = departments.reduce((sum, d) => sum + d.total, 0);
  const completedTasks = departments.reduce((sum, d) => sum + d.completed, 0);
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} className="rounded-lg text-slate-500 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">{project.name}</h1>
              {user?.role === 'admin' && (
                <Button variant="ghost" size="icon" onClick={openEditProjectDialog} className="rounded h-7 w-7 text-slate-400 hover:text-slate-700" data-testid="edit-project-btn">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              )}
              <Badge className={`rounded-md text-xs ${completionRate === 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : completionRate > 50 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                {completionRate}%
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{project.product} &bull; {project.location} &bull; {completedTasks}/{totalTasks} tasks</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[140px] h-9 rounded-lg border-slate-200 text-xs" data-testid="sort-select">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="due_date">Sort by Date</SelectItem>
              <SelectItem value="department">Sort by Dept</SelectItem>
              <SelectItem value="status">Sort by Status</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleDownloadExcel} className="rounded-lg gap-1.5 border-slate-300 h-9 text-xs" data-testid="download-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" />Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadReport} className="rounded-lg gap-1.5 border-slate-300 h-9 text-xs" data-testid="download-report-btn">
            <Download className="w-3.5 h-3.5" />PDF
          </Button>
          {user?.role === 'admin' && (
            <Button variant="outline" size="sm" onClick={handlePublish} disabled={publishing || totalTasks === 0} className="rounded-lg gap-1.5 border-slate-300 h-9 text-xs" data-testid="publish-btn">
              <Send className="w-3.5 h-3.5" />{publishing ? 'Publishing...' : (project.published ? 'Republish' : 'Publish')}
            </Button>
          )}
          <Button size="sm" onClick={() => { resetForm(); setShowDialog(true); }} className="rounded-lg gap-1.5 bg-[#1e3a5f] hover:bg-[#152a47] text-white h-9 text-xs" data-testid="add-task-btn">
            <Plus className="w-4 h-4" />Add Task
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={(v) => { setActiveView(v); if (v === 'gantt') fetchGanttData(); }}>
        <TabsList className="bg-slate-100 p-0.5 h-9">
          <TabsTrigger value="tasks" className="gap-1.5 text-xs data-[state=active]:bg-white h-8"><List className="w-3.5 h-3.5" />Tasks</TabsTrigger>
          <TabsTrigger value="gantt" className="gap-1.5 text-xs data-[state=active]:bg-white h-8"><BarChart3 className="w-3.5 h-3.5" />Gantt</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-4 space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="py-4 px-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#1e3a5f]/10 rounded-lg"><Target className="w-5 h-5 text-[#1e3a5f]" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Earliest Opening</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {earliestOpeningDate ? new Date(earliestOpeningDate).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : 'No tasks'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="py-4 px-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg"><Flag className="w-5 h-5 text-amber-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Critical Path</p>
                    <p className="text-sm font-semibold text-slate-900">{criticalTaskCount} task{criticalTaskCount !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress bar */}
          <Card className="bg-white border-slate-200">
            <CardContent className="py-4 px-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600 font-medium">Progress</span>
                <span className="text-sm text-slate-500">{completedTasks}/{totalTasks}</span>
              </div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#1e3a5f] rounded-full transition-all duration-500" style={{ width: `${completionRate}%` }} />
              </div>
            </CardContent>
          </Card>

          {/* Tasks by Department */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="space-y-4">
              {departments.length === 0 ? (
                <Card className="bg-white border-slate-200">
                  <CardContent className="py-12 text-center">
                    <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No tasks yet. Add the first task to get started!</p>
                    <Button onClick={() => { resetForm(); setShowDialog(true); }} className="mt-4 bg-[#1e3a5f] hover:bg-[#152a47] text-white">Add First Task</Button>
                  </CardContent>
                </Card>
              ) : (
                departments.map((dept) => (
                  <Collapsible key={dept.department} open={expandedDepts[dept.department]} onOpenChange={() => toggleDept(dept.department)}>
                    <Card className="bg-white border-slate-200 overflow-hidden">
                      <CollapsibleTrigger asChild>
                        <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors py-4 px-5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {expandedDepts[dept.department] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                              <CardTitle className="text-base font-semibold text-slate-900">{dept.department}</CardTitle>
                              <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 rounded-md text-xs px-2">{dept.completed}/{dept.total}</Badge>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#1e3a5f] rounded-full" style={{ width: `${dept.total > 0 ? (dept.completed / dept.total) * 100 : 0}%` }} />
                              </div>
                              <span className="text-xs text-slate-500 w-8 text-right">{dept.total > 0 ? Math.round((dept.completed / dept.total) * 100) : 0}%</span>
                            </div>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="pt-0 px-4 pb-4">
                          <SortableContext items={dept.tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-3">
                              {dept.tasks.map((task) => (
                                <SortableTaskItem key={task.id} task={task} user={user} handleStatusChange={handleStatusChange} openEditDialog={openEditDialog} handleDeleteTask={handleDeleteTask} />
                              ))}
                            </div>
                          </SortableContext>
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                ))
              )}
            </div>
          </DndContext>
        </TabsContent>

        {/* Gantt Chart Tab */}
        <TabsContent value="gantt" className="mt-4">
          {ganttData ? (
            <Card className="bg-white border-slate-200">
              <CardHeader className="py-4">
                <CardTitle className="text-base text-slate-800">Project Timeline</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  {ganttData.min_date && ganttData.max_date ? `${format(parseISO(ganttData.min_date), 'dd MMM yyyy')} - ${format(parseISO(ganttData.max_date), 'dd MMM yyyy')}` : 'No timeline data'}
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                {ganttData.tasks.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No tasks with dates</p>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-[700px]">
                      <div className="grid grid-cols-[200px_1fr] gap-4 border-b border-slate-200 pb-3 mb-4">
                        <div className="font-semibold text-sm text-slate-700">Task</div>
                        <div className="font-semibold text-sm text-slate-700">Timeline</div>
                      </div>
                      <div className="space-y-2">
                        {ganttData.tasks.map((task) => {
                          const minDate = ganttData.min_date ? parseISO(ganttData.min_date) : new Date();
                          const maxDate = ganttData.max_date ? parseISO(ganttData.max_date) : new Date();
                          const totalDays = Math.max(1, differenceInDays(maxDate, minDate) + 1);
                          const taskStart = task.start_date ? parseISO(task.start_date) : parseISO(task.end_date);
                          const taskEnd = parseISO(task.end_date);
                          const startOffset = Math.max(0, differenceInDays(taskStart, minDate));
                          const duration = Math.max(1, differenceInDays(taskEnd, taskStart) + 1);
                          const leftPercent = (startOffset / totalDays) * 100;
                          const widthPercent = Math.min((duration / totalDays) * 100, 100 - leftPercent);
                          const statusColors = { completed: 'bg-emerald-500', in_progress: 'bg-blue-500', pending: 'bg-amber-500' };
                          return (
                            <div key={task.id} className="grid grid-cols-[200px_1fr] gap-4 items-center py-2 border-b border-slate-50 last:border-0">
                              <div className="truncate">
                                <span className="text-sm text-slate-800">{task.title}</span>
                                <div className="text-xs text-slate-400 truncate">{task.department} &bull; {task.assignee}</div>
                              </div>
                              <div className="relative h-7 bg-slate-100 rounded overflow-hidden">
                                <div className={`absolute h-full ${statusColors[task.status] || 'bg-slate-400'} rounded transition-all`} style={{ left: `${leftPercent}%`, width: `${Math.max(widthPercent, 2)}%` }}>
                                  <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-medium truncate px-1">{task.duration}d</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-4 mt-4 pt-4 border-t border-slate-200">
                        {[['bg-emerald-500','Completed'],['bg-blue-500','In Progress'],['bg-amber-500','Pending']].map(([c,l]) => (
                          <div key={l} className="flex items-center gap-1.5"><div className={`w-3 h-3 rounded ${c}`} /><span className="text-xs text-slate-500">{l}</span></div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1e3a5f]" /></div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Task Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg bg-white border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">{editingTask ? 'Edit Task' : 'Add New Task'}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">{editingTask ? 'Update task details' : 'Add a new task to this project'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Task Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Enter task title" className="rounded-lg h-11 border-slate-200" data-testid="task-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter description" className="rounded-lg min-h-[70px] border-slate-200" data-testid="task-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm">Department *</Label>
                <Select value={formData.department} onValueChange={(value) => setFormData({ ...formData, department: value })}>
                  <SelectTrigger className="rounded-lg h-11 border-slate-200" data-testid="task-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {allDepartments.map(dept => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm">Assignees *</Label>
                <AssigneeMultiSelect users={users} selectedIds={formData.assignee_ids} onChange={(ids) => setFormData({ ...formData, assignee_ids: ids })} />
              </div>
            </div>

            {/* Date Range - Native inputs for iOS compatibility */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm">Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="rounded-lg h-11 border-slate-200"
                  data-testid="task-start-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm">Due Date *</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => handleDueDateChange(e.target.value)}
                  className="rounded-lg h-11 border-slate-200"
                  data-testid="task-due-date-input"
                />
              </div>
            </div>

            {showDateChangeReason && (
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm">Reason for Earlier Date</Label>
                <Input value={formData.date_change_reason} onChange={(e) => setFormData({ ...formData, date_change_reason: e.target.value })} placeholder="Why is the date being moved?" className="rounded-lg h-11 border-slate-200" />
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Starts After Task</Label>
              <Select value={formData.depends_on || "none"} onValueChange={(value) => setFormData({ ...formData, depends_on: value === "none" ? "" : value })}>
                <SelectTrigger className="rounded-lg h-11 border-slate-200"><SelectValue placeholder="Select predecessor" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-[200px]">
                  <SelectItem value="none">No dependency</SelectItem>
                  {allTasks.filter(t => t.id !== editingTask?.id).map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title} ({t.department})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-lg border-slate-300">Cancel</Button>
              <Button type="submit" className="rounded-lg bg-[#1e3a5f] hover:bg-[#152a47] text-white" data-testid="save-task-btn">{editingTask ? 'Update Task' : 'Add Task'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProjectDialog} onOpenChange={setShowEditProjectDialog}>
        <DialogContent className="sm:max-w-[425px] bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">Edit Project</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Update the project name.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditProject} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Product *</Label>
              <Input value={projectFormData.product} onChange={(e) => setProjectFormData({ ...projectFormData, product: e.target.value })} className="rounded-lg h-11 border-slate-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Location *</Label>
              <Input value={projectFormData.location} onChange={(e) => setProjectFormData({ ...projectFormData, location: e.target.value })} className="rounded-lg h-11 border-slate-200" />
            </div>
            <p className="text-sm text-slate-500">Name: <strong>{projectFormData.product} {projectFormData.location}</strong></p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditProjectDialog(false)} className="rounded-lg border-slate-300">Cancel</Button>
              <Button type="submit" className="rounded-lg bg-[#1e3a5f] hover:bg-[#152a47] text-white">Update</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}